import { SoundFont2 as EsmSoundFont2 } from "soundfont2-esm";

/**
 * Минимальный набор полей из smplr-овского `Sf2`, который мы потребляем.
 *
 * `smplr` объявляет `Sf2`, `Sf2Instrument`, `Sf2Zone`, `Sf2Sample` как
 * локальные типы (НЕ экспортирует), но контракт стабилен: `loadInstrument`
 * внутри smplr ищет инструмент по `header.name` и берёт `zones[*].sample`.
 * Дублируем эти структуры здесь — адаптер один и тот же, но мы не зависим
 * от того, экспортирует ли smplr свои типы.
 */
type Sf2Sample = {
  data: Int16Array;
  header: {
    name: string;
    sampleRate: number;
    originalPitch: number;
    pitchCorrection: number;
    start: number;
    end: number;
    startLoop: number;
    endLoop: number;
  };
};

type Sf2Zone = {
  sample: Sf2Sample;
  keyRange?: { lo: number; hi: number };
};

type Sf2Instrument = {
  header: { name: string };
  zones: Sf2Zone[];
};

type Sf2 = {
  instruments: Sf2Instrument[];
};

type EsmRange = { lo: number; hi: number };

type EsmSampleHeader = {
  name: string;
  sampleRate: number;
  originalPitch: number;
  pitchCorrection: number;
  start: number;
  end: number;
  startLoop: number;
  endLoop: number;
  type?: unknown;
  link?: unknown;
};

type EsmSample = { data: Int16Array; header: EsmSampleHeader };

type EsmInstrumentZone = {
  keyRange?: EsmRange;
  velRange?: EsmRange;
  modulators?: unknown;
  generators?: unknown;
  sample: EsmSample;
};

type EsmInstrument = {
  header: { name: string; bagIndex?: number };
  zones: EsmInstrumentZone[];
};

const cloneSample = (sample: EsmSample): Sf2Sample => ({
  data: sample.data,
  header: {
    name: sample.header.name,
    sampleRate: sample.header.sampleRate,
    originalPitch: sample.header.originalPitch,
    pitchCorrection: sample.header.pitchCorrection,
    start: sample.header.start,
    end: sample.header.end,
    startLoop: sample.header.startLoop,
    endLoop: sample.header.endLoop,
  },
});

const toSf2Zone = (zone: EsmInstrumentZone): Sf2Zone => {
  const out: Sf2Zone = {
    sample: cloneSample(zone.sample),
  };
  if (zone.keyRange) {
    out.keyRange = { lo: zone.keyRange.lo, hi: zone.keyRange.hi };
  }
  return out;
};

const toSf2Instrument = (inst: EsmInstrument): Sf2Instrument => ({
  header: { name: inst.header.name },
  zones: inst.zones.map(toSf2Zone),
});

/**
 * Ключ для кеша парсера. FNV-1a-подобный 32-bit хеш по всему массиву —
 * для мегабайтных SF2 это всё ещё микросекунды (Uint8Array чтение O(n)
 * без аллокаций). Берём весь массив, чтобы mock-данные в тестах с
 * одинаковой длиной/границами, но разным содержимым в середине, не
 * давали ложных коллизий.
 */
const fingerprintKey = (data: Uint8Array): string => {
  let h = 0x811c9dc5;
  for (let i = 0; i < data.length; i++) {
    h ^= data[i]!;
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return `${data.length}:${h.toString(16)}`;
};

// Один источник SF2 может породить N инстансов `Soundfont2` (по одному
// на уникальный `(program, isDrum)` канал). smplr вызывает
// `createSoundfont` per-instance — то есть без кеша мы парсим один и тот
// же SF2 N раз подряд, что для GeneralUser_GS (~30 МБ) занимает секунды
// main-thread CPU на первой загрузке. LRU ограничиваем 4 записями —
// этого хватает для UI-плеера (в худшем случае 1 источник + 3 file-picker'а).
const PARSE_CACHE_MAX = 4;
const parseCache = new Map<string, Sf2>();

const getCached = (key: string): Sf2 | undefined => {
  const v = parseCache.get(key);
  if (v) {
    // Refresh LRU position.
    parseCache.delete(key);
    parseCache.set(key, v);
  }
  return v;
};

const setCached = (key: string, value: Sf2): void => {
  if (parseCache.has(key)) parseCache.delete(key);
  parseCache.set(key, value);
  while (parseCache.size > PARSE_CACHE_MAX) {
    const first = parseCache.keys().next().value;
    if (first !== undefined) parseCache.delete(first);
  }
};

/**
 * Адаптер между `soundfont2-esm` и smplr-овским типом `Sf2`.
 *
 * `smplr.Soundfont2` принимает `createSoundfont: (data) => Sf2` — мы отдаём
 * ровно тот формат, который ожидает `sf2InstrumentToPreset` внутри smplr:
 * `{ instruments: [{ header: { name }, zones: [{ sample, keyRange? }] }] }`.
 *
 * Парсер `soundfont2-esm` даёт `instruments[i].header.name` (имя инструмента
 * из под-чанка `inst`), а smplr-овский `loadInstrument(name)` ищет
 * инструмент именно по этому полю. То есть маппинг имени сохраняется
 * 1-к-1 — внутри адаптера подменять нечего, но мы изолируем парсер
 * в один модуль, чтобы при апгрейдах `soundfont2-esm` / `smplr`
 * расхождения всплыли в тестах, а не в продакшене.
 */
export const parseSf2 = (data: Uint8Array): Sf2 => {
  const key = fingerprintKey(data);
  const cached = getCached(key);
  if (cached) return cached;
  const parsed = new EsmSoundFont2(data);
  const instruments = (parsed.instruments as unknown as EsmInstrument[]).map(
    toSf2Instrument
  );
  const out: Sf2 = { instruments };
  setCached(key, out);
  return out;
};